import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { useAuthStore } from './useAuthStore'

export type AttentionPointType =
  | 'BLOCAGE'
  | 'SUJET_SENSIBLE'
  | 'ETUDE_FAISABILITE'
  | 'SUJET_TRANSVERSE'
  | 'AUTRE'

export type AttentionPointStatus = 'OUVERT' | 'EN_COURS' | 'LEVE'

export type AttentionPointCriticite = 'FAIBLE' | 'MOYENNE' | 'ELEVEE' | 'CRITIQUE'

export type JournalEntryKind = 'COMMENTAIRE' | 'DETAIL' | 'ACTION'

export interface AttentionInterlocutor {
  kind: 'INTERNE' | 'EXTERNE'
  userUuid?: string
  firstName?: string
  lastName?: string
  email?: string
  company?: string
}

interface AttentionResponsable {
  userUuid: string
  firstName?: string
  lastName?: string
  email?: string
}

interface AttentionJournalEntry {
  id: string
  kind: JournalEntryKind
  message: string
  at: string
  userUuid: string
  firstName?: string
  lastName?: string
}

interface AttentionStatusEntry {
  status: AttentionPointStatus
  userUuid: string
  firstName?: string
  lastName?: string
  at: string
  note?: string
}

interface AttentionResolution {
  comment: string
  at: string
  userUuid: string
  firstName?: string
  lastName?: string
}

export interface AttentionPoint {
  _id: string
  code: string
  title: string
  type: AttentionPointType
  description: string
  impact: string
  criticite: AttentionPointCriticite
  status: AttentionPointStatus
  domaines: string[]
  staleThresholdDays: number
  dateEntree: string
  dateDebut?: string
  resolutionPrevue?: string
  dateFin?: string
  responsable?: AttentionResponsable
  interlocuteurs: AttentionInterlocutor[]
  journal: AttentionJournalEntry[]
  resolution?: AttentionResolution
  statusHistory: AttentionStatusEntry[]
  createdAt: string
  updatedAt: string
}

export interface AttentionStats {
  total: number
  ouverts: number
  statusCounts: Partial<Record<AttentionPointStatus, number>>
  criticiteCounts: Partial<Record<AttentionPointCriticite, number>>
  overdue: number
}

export interface AttentionFilters {
  status?: AttentionPointStatus[]
  type?: AttentionPointType[]
  criticite?: AttentionPointCriticite[]
  search?: string
  responsableUuid?: string
  domaine?: string
  overdue?: boolean
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export interface CreateAttentionPointBody {
  title: string
  type: AttentionPointType
  description?: string
  impact?: string
  criticite?: AttentionPointCriticite
  domaines?: string[]
  staleThresholdDays?: number
  dateEntree?: string
  dateDebut?: string
  resolutionPrevue?: string
  responsableUuid?: string
  interlocuteurs: AttentionInterlocutor[]
}

export type UpdateAttentionPointBody = Partial<
  Omit<CreateAttentionPointBody, 'interlocuteurs' | 'responsableUuid'>
> & {
  interlocuteurs?: AttentionInterlocutor[]
  responsableUuid?: string | null
}

export const useAttentionPointsStore = defineStore('attentionPoints', () => {
  const suiteStore = useSuiteStore()
  const authStore = useAuthStore()

  const points = ref<AttentionPoint[]>([])
  const total = ref(0)
  const page = ref(1)
  const pageSize = ref(50)
  const point = ref<AttentionPoint | null>(null)
  const stats = ref<AttentionStats | null>(null)
  const loading = ref(false)

  function buildQuery(filters: AttentionFilters): string {
    const params = new URLSearchParams()
    filters.status?.forEach((s) => params.append('status', s))
    filters.type?.forEach((t) => params.append('type', t))
    filters.criticite?.forEach((c) => params.append('criticite', c))
    if (filters.search) params.append('search', filters.search)
    if (filters.responsableUuid) params.append('responsableUuid', filters.responsableUuid)
    if (filters.domaine) params.append('domaine', filters.domaine)
    if (filters.overdue) params.append('overdue', 'true')
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    return params.toString()
  }

  function mutationHeaders() {
    return {
      'Content-Type': 'application/json',
      'x-csrf-token': authStore.getCoreCsrfToken(),
    }
  }

  async function fetchAll(filters: AttentionFilters = {}) {
    loading.value = true
    try {
      const qs = buildQuery({ page: 1, pageSize: 50, ...filters })
      const res = await suiteStore.suiteFetch(`/attention-points${qs ? `?${qs}` : ''}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération des points d\'attention')
      const data = await res.json()
      points.value = data.items ?? []
      total.value = data.total ?? 0
      page.value = data.page ?? 1
      pageSize.value = data.pageSize ?? 50
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchStats() {
    const res = await suiteStore.suiteFetch('/attention-points/stats', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
    if (!res || !res.ok) throw new Error('Echec de la récupération des compteurs')
    stats.value = (await res.json()) as AttentionStats
    return stats.value
  }

  async function fetchOne(id: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la récupération du point d\'attention')
      point.value = await res.json()
      return point.value
    } finally {
      loading.value = false
    }
  }

  async function create(body: CreateAttentionPointBody) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch('/attention-points', {
        method: 'POST',
        headers: mutationHeaders(),
        credentials: 'include',
        body: JSON.stringify(body),
      })
      if (!res || !res.ok) {
        const err = res && typeof res !== 'boolean' ? await res.json().catch(() => ({})) : {}
        throw new Error((err as { message?: string })?.message ?? 'Echec de la création')
      }
      return (await res.json()) as AttentionPoint
    } finally {
      loading.value = false
    }
  }

  async function update(id: string, patch: UpdateAttentionPointBody) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}`, {
        method: 'PATCH',
        headers: mutationHeaders(),
        credentials: 'include',
        body: JSON.stringify(patch),
      })
      if (!res || !res.ok) {
        const err = res && typeof res !== 'boolean' ? await res.json().catch(() => ({})) : {}
        throw new Error((err as { message?: string })?.message ?? 'Echec de la mise à jour')
      }
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function addJournalEntry(id: string, kind: JournalEntryKind, message: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}/journal`, {
        method: 'POST',
        headers: mutationHeaders(),
        credentials: 'include',
        body: JSON.stringify({ kind, message }),
      })
      if (!res || !res.ok) throw new Error('Echec de l\'ajout au journal')
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function changeStatus(id: string, status: AttentionPointStatus, note?: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}/status`, {
        method: 'POST',
        headers: mutationHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status, note }),
      })
      if (!res || !res.ok) throw new Error('Echec du changement de statut')
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function resolve(id: string, comment: string, dateFin?: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}/resolve`, {
        method: 'POST',
        headers: mutationHeaders(),
        credentials: 'include',
        body: JSON.stringify({ comment, dateFin }),
      })
      if (!res || !res.ok) throw new Error('Echec de la levée du point')
      await fetchOne(id)
    } finally {
      loading.value = false
    }
  }

  async function remove(id: string) {
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`/attention-points/${id}`, {
        method: 'DELETE',
        headers: mutationHeaders(),
        credentials: 'include',
      })
      if (!res || !res.ok) throw new Error('Echec de la suppression')
    } finally {
      loading.value = false
    }
  }

  async function exportPoints(filters: AttentionFilters & { format?: 'csv' | 'xlsx' } = {}) {
    const qs = buildQuery(filters)
    const params = qs ? `?${qs}&format=${filters.format ?? 'xlsx'}` : `?format=${filters.format ?? 'xlsx'}`
    const res = await suiteStore.suiteFetch(`/attention-points/export${params}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!res || !res.ok) throw new Error('Echec de l\'export')
    const blob = await res.blob()
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="?([^";]+)"?/)?.[1] ??
      `points-attention.${filters.format ?? 'xlsx'}`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return {
    points,
    total,
    page,
    pageSize,
    point,
    stats,
    loading,
    fetchAll,
    fetchStats,
    fetchOne,
    create,
    update,
    addJournalEntry,
    changeStatus,
    resolve,
    remove,
    exportPoints,
  }
})
